import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Button, IconButton } from "@material-tailwind/react";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/solid";
import axios from 'axios';
import { rootUrl } from '../../../../config/config';
import { useAuth } from '../../AuthContext';
import { Alerty } from '../../../common/Alerty';

const BulkPostDriverTransactions = () => {
    const { user } = useAuth();
    const [drivers, setDrivers] = useState([]);
    const [transTypes, setTransTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [successOpen, setSuccessOpen] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [rows, setRows] = useState([
        { id: 1, driver_id: '', trans_type: '', date: new Date().toISOString().split('T')[0], amount: '', notes: '', your_ref: '' }
    ]);

    useEffect(() => {
        fetchDrivers();
        fetchTransTypes();
    }, []);

    const fetchDrivers = async () => {
        try {
            const res = await axios.get(`${rootUrl}/drivers?limit=1000`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setDrivers(res.data.data || []);
        } catch (error) {
            console.error('Error fetching drivers:', error);
        }
    };

    const fetchTransTypes = async () => {
        try {
            const res = await axios.get(`${rootUrl}/payments/driver-types`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setTransTypes(res.data.data || []);
        } catch (error) {
            console.error('Error fetching transaction types:', error);
        }
    };

    const addRow = () => {
        setRows([...rows, { 
            id: Date.now(), 
            driver_id: '', 
            trans_type: '', 
            date: new Date().toISOString().split('T')[0], 
            amount: '', 
            notes: '', 
            your_ref: '' 
        }]);
    };

    const removeRow = (id) => {
        if (rows.length > 1) {
            setRows(rows.filter(row => row.id !== id));
        }
    };

    const updateRow = (id, field, value) => {
        setRows(rows.map(row => row.id === id ? { ...row, [field]: value } : row));
    };

    const handleSubmit = async () => {
        // Validate
        const invalidRows = rows.filter(r => !r.driver_id || !r.trans_type || !r.date || !r.amount);
        if (invalidRows.length > 0) {
            setErrorMessage('Please fill in Driver, Type, Date, and Amount for all rows');
            setErrorOpen(true);
            return;
        }

        setLoading(true);
        try {
            const transactions = rows.map(row => ({
                driver_id: row.driver_id,
                trans_type: row.trans_type,
                date: row.date,
                amount: parseFloat(row.amount),
                notes: row.notes || '',
                your_ref: row.your_ref || '',
                input_by: user?.username || 'system',
                edit_by: user?.username || 'system'
            }));

            const res = await axios.post(`${rootUrl}/drivers/transactions/bulk`, { transactions }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            if (res.data.success) {
                const successCount = res.data.results.filter(r => r.success).length;
                const failCount = res.data.results.filter(r => !r.success).length;
                
                if (failCount === 0) {
                    setSuccessMessage(`All ${successCount} transactions posted successfully!`);
                    setSuccessOpen(true);
                    setRows([{ id: Date.now(), driver_id: '', trans_type: '', date: new Date().toISOString().split('T')[0], amount: '', notes: '', your_ref: '' }]);
                } else {
                    setErrorMessage(`${successCount} succeeded, ${failCount} failed. Check console for details.`);
                    setErrorOpen(true);
                    console.log('Failed transactions:', res.data.results.filter(r => !r.success));
                }
            }
        } catch (error) {
            console.error('Error bulk posting:', error);
            setErrorMessage(error.response?.data?.error || error.message);
            setErrorOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] min-h-screen p-6">
            {/* Header Section */}
            <div className="text-center mb-8">
                <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Bulk Post Driver Transactions</h1>
                <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                <p className="text-lg text-[#2a2e30] opacity-80">
                    Post multiple driver transactions at once
                </p>
            </div>

            <div className="max-w-7xl mx-auto">
                <Card className="bg-white rounded-2xl shadow-xl p-6">
                    <CardBody>
                        <div className="flex justify-between items-center mb-6">
                            <Typography variant="h5" color="blue-gray" className="font-semibold">
                                Transaction Entries
                            </Typography>
                            <Button 
                                color="green" 
                                size="sm" 
                                className="flex items-center gap-2"
                                onClick={addRow}
                            >
                                <PlusIcon className="h-4 w-4" /> Add Row
                            </Button>
                        </div>
                        
                        <Alerty open={successOpen} setOpen={setSuccessOpen} color="green" message={successMessage} variant="solid" />
                        <Alerty open={errorOpen} setOpen={setErrorOpen} color="red" message={errorMessage} variant="solid" />

                        <div className="overflow-x-auto mt-4">
                            <table className="w-full min-w-max table-auto text-left border-collapse">
                                <thead>
                                    <tr className="border-b-2 border-blue-gray-200">
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">#</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Driver *</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Type *</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Date *</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Amount *</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Reference</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm">Notes</th>
                                        <th className="p-3 bg-blue-gray-50 font-bold text-sm text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, index) => (
                                        <tr key={row.id} className="border-b border-blue-gray-100 hover:bg-blue-gray-50 transition-colors">
                                            <td className="p-3 text-sm font-semibold text-gray-600">{index + 1}</td>
                                            <td className="p-2">
                                                <select
                                                    value={row.driver_id}
                                                    onChange={(e) => updateRow(row.id, 'driver_id', e.target.value)}
                                                    className="w-full px-3 py-2 border border-blue-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-transparent"
                                                >
                                                    <option value="">Select Driver</option>
                                                    {drivers.map(d => (
                                                        <option key={d.id} value={d.id_no}>{d.driver_name}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="p-2">
                                                <select
                                                    value={row.trans_type}
                                                    onChange={(e) => updateRow(row.id, 'trans_type', e.target.value)}
                                                    className="w-full px-3 py-2 border border-blue-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-transparent"
                                                >
                                                    <option value="">Select Type</option>
                                                    {transTypes.map(t => (
                                                        <option key={t.type} value={t.type}>{t.short_desc}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="p-2">
                                                <input
                                                    type="date"
                                                    value={row.date}
                                                    onChange={(e) => updateRow(row.id, 'date', e.target.value)}
                                                    className="w-full px-3 py-2 border border-blue-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-transparent"
                                                />
                                            </td>
                                            <td className="p-2">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={row.amount}
                                                    onChange={(e) => updateRow(row.id, 'amount', e.target.value)}
                                                    placeholder="0.00"
                                                    className="w-full px-3 py-2 border border-blue-gray-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-transparent"
                                                />
                                            </td>
                                            <td className="p-2">
                                                <input
                                                    type="text"
                                                    value={row.your_ref}
                                                    onChange={(e) => updateRow(row.id, 'your_ref', e.target.value)}
                                                    placeholder="Reference"
                                                    className="w-full px-3 py-2 border border-blue-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-transparent"
                                                />
                                            </td>
                                            <td className="p-2">
                                                <input
                                                    type="text"
                                                    value={row.notes}
                                                    onChange={(e) => updateRow(row.id, 'notes', e.target.value)}
                                                    placeholder="Notes"
                                                    className="w-full px-3 py-2 border border-blue-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733] focus:border-transparent"
                                                />
                                            </td>
                                            <td className="p-2 text-center">
                                                <IconButton 
                                                    color="red" 
                                                    size="sm" 
                                                    onClick={() => removeRow(row.id)}
                                                    disabled={rows.length === 1}
                                                    className="rounded-lg"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-6 flex justify-between items-center border-t border-blue-gray-200 pt-6">
                            <div className="flex items-center gap-4">
                                <Typography variant="small" color="blue-gray" className="font-normal">
                                    <span className="text-red-500">*</span> Required fields
                                </Typography>
                                <Typography variant="small" color="blue-gray" className="font-semibold">
                                    Total Rows: <span className="text-[#FF5733]">{rows.length}</span>
                                </Typography>
                            </div>
                            <Button 
                                color="orange" 
                                onClick={handleSubmit} 
                                disabled={loading || rows.length === 0}
                                className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#e64a2b]"
                            >
                                {loading ? 'Processing...' : `Submit ${rows.length} Transaction${rows.length !== 1 ? 's' : ''}`}
                            </Button>
                        </div>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default BulkPostDriverTransactions;